import { useQuery } from '@tanstack/react-query';
import { supabase } from '../api';

type CollectionType = {
  id: number,
  type: string,
  name: string,
  icon: string,
  items: any[],
}

const getCollection = async (id: number, page: number, limit: number): Promise<CollectionType> => {
  const start = (page - 1) * limit;

  const { data: colData, error: colError } = await supabase
    .from('collections')
    .select('id, name, type, icon')
    .eq('id', id)
    .single();

  if (colError) {
    throw new Error(colError.message);
  }

  const type = colData?.type;
  let query;

  if (type === 'assets') {
    const { data: activeAssets } = await supabase
      .from('assets')
      .select('id')
      .eq('active', true)
    const activeAssetIds = activeAssets?.map((asset) => asset.id) as string[];

    query = supabase
      .from('collections_assets')
      .select(`
        *,
        assets:assets (
          *,
          category:categories(*),
          posts:posts_assets(*)
        )
      `)
      .in('asset_id', activeAssetIds)
      .eq('collection_id', id)
      .range(start, start + limit - 1);
  }

  if (type === 'hashtags') {
    query = supabase
      .from('collections_hashtags')
      .select(`
        *,
        hashtags:hashtags (*)
      `)
      .eq('collection_id', id)
      .range(start, start + limit - 1);
  }

  if (type === 'experts') {
    query = supabase
      .from('collections_experts')
      .select(`
        *,
        experts:user_profiles (
          *,
          expertProfile:salons_profile (*)
        )
      `)
      .eq('collection_id', id)
      .range(start, start + limit - 1);
  }

  if (!query) {
    throw new Error(`Unknown collection type ${type}`);
  }

  const { data, error } = await query;

  if (error) {
    throw new Error(error.message);
  }

  return { ...colData, items: (data || []).map((v) => v[type]) };
};

export default function useCollection(id: number, page: number = 1, limit: number = 10) {
  return useQuery({
    queryKey: ['collection', id, page],
    queryFn: () => getCollection(id, page, limit),
    staleTime: 0,
    cacheTime: 0,
  });
}
